import { getRouteVisitId, subscribeToRouteVisit } from "~/lib/route-visit";
import {
  sanitizeTelemetryIdentity,
  sanitizeTelemetryProperties,
} from "./privacy";
import type {
  SanitizedTelemetryProperties,
  TelemetryEventName,
  TelemetryEventProperties,
  TelemetryIdentity,
  TelemetryProviderIdentity,
  TelemetryProvider,
} from "./events";

type TelemetryGlobalContext = Readonly<Record<string, unknown>>;

const providers = new Map<string, TelemetryProvider>();

let globalContext: TelemetryGlobalContext = {};
let currentRawIdentityKey = createRawIdentityKey({
  authenticated: false,
  authSource: "guest",
  canonicalUserId: null,
  privyUserId: null,
  walletAddress: null,
});
let currentIdentity: TelemetryProviderIdentity = sanitizeTelemetryIdentity({
  authenticated: false,
  authSource: "guest",
  canonicalUserId: null,
  privyUserId: null,
  walletAddress: null,
});
let unsubscribeRouteVisit: (() => void) | null = null;

function createRawIdentityKey(identity: TelemetryIdentity): string {
  return [
    identity.authenticated ? "authenticated" : "guest",
    identity.authSource,
    identity.canonicalUserId ?? "anonymous",
    identity.privyUserId ?? "no-privy-id",
    identity.walletAddress ?? "no-wallet",
  ].join(":");
}

function reportProviderDispatchError(
  provider: TelemetryProvider,
  error: unknown,
): void {
  if (import.meta.env.DEV) {
    console.warn(
      `[telemetry] provider dispatch failed: ${provider.name}`,
      error,
    );
  }
}

function dispatchProvider(
  provider: TelemetryProvider,
  operation: () => Promise<void> | void,
): void {
  try {
    void Promise.resolve(operation()).catch((error: unknown) => {
      reportProviderDispatchError(provider, error);
    });
  } catch (error) {
    reportProviderDispatchError(provider, error);
  }
}

function ensureRouteVisitSubscription(): void {
  if (unsubscribeRouteVisit) {
    return;
  }

  unsubscribeRouteVisit = subscribeToRouteVisit((routeVisitId) => {
    setTelemetryContext({
      route_visit_id: routeVisitId,
    });
  });
}

export function registerTelemetryProvider(provider: TelemetryProvider): void {
  ensureRouteVisitSubscription();
  providers.set(provider.name, provider);

  if (provider.identify) {
    dispatchProvider(provider, () => provider.identify?.(currentIdentity));
  }
}

export function setTelemetryContext(context: TelemetryGlobalContext): void {
  globalContext = {
    ...globalContext,
    ...context,
  };
}

export function identifyTelemetryUser(identity: TelemetryIdentity): void {
  const rawIdentityKey = createRawIdentityKey(identity);
  if (rawIdentityKey === currentRawIdentityKey) {
    return;
  }

  const sanitizedIdentity = sanitizeTelemetryIdentity(identity);
  currentRawIdentityKey = rawIdentityKey;
  currentIdentity = sanitizedIdentity;

  for (const provider of providers.values()) {
    if (provider.identify) {
      dispatchProvider(provider, () => provider.identify?.(currentIdentity));
    }
  }
}

export function resetTelemetryUser(): void {
  currentRawIdentityKey = createRawIdentityKey({
    authenticated: false,
    authSource: "guest",
    canonicalUserId: null,
    privyUserId: null,
    walletAddress: null,
  });
  currentIdentity = sanitizeTelemetryIdentity({
    authenticated: false,
    authSource: "guest",
    canonicalUserId: null,
    privyUserId: null,
    walletAddress: null,
  });

  for (const provider of providers.values()) {
    if (provider.reset) {
      dispatchProvider(provider, () => provider.reset?.());
    }
  }
}

export function trackTelemetryEvent<N extends TelemetryEventName>(
  name: N,
  properties: TelemetryEventProperties<N>,
): void {
  ensureRouteVisitSubscription();
  const eventProperties: SanitizedTelemetryProperties =
    sanitizeTelemetryProperties({
      ...globalContext,
      ...properties,
      route_visit_id: getRouteVisitId(),
    });
  for (const provider of providers.values()) {
    dispatchProvider(provider, () =>
      provider.track({
        name,
        properties: eventProperties,
      }),
    );
  }
}

export function resetTelemetryForTests(): void {
  providers.clear();
  unsubscribeRouteVisit?.();
  unsubscribeRouteVisit = null;
  globalContext = {
    route_visit_id: getRouteVisitId(),
  };
  currentRawIdentityKey = createRawIdentityKey({
    authenticated: false,
    authSource: "guest",
    canonicalUserId: null,
    privyUserId: null,
    walletAddress: null,
  });
  currentIdentity = sanitizeTelemetryIdentity({
    authenticated: false,
    authSource: "guest",
    canonicalUserId: null,
    privyUserId: null,
    walletAddress: null,
  });
}
